import { Attribute } from '../../decorators/attribute.decorator';
import { element } from '../../global/template-literals';
import { Component } from '../../decorators/component.decorator';
import { UIElement } from '../ui-element';
import { Child } from '../../decorators/child.decorator';
import {
    createElement,
    removeAttributes,
    setAttributes,
} from '../../global/ui-helpers';
import { ComponentState } from '../ui-element.const';
import styles from './ui-header-button.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIHeaderButton
 * @element ui-header-button
 * @classdesc Represents a class for <code>ui-header-button</code> element.
 * @property {string} label {@attr label} Button label.
 * @property {UIHeaderButtonLayout} layout {@attr layout} Button layout type. Primary is default.
 *  {@desc primary}
 *  {@desc tonal}
 *  {@desc tertiary}
 * @property {string} [glyph] {@attr glyph} Icon glyph code.
 * @property {boolean} [showDropdown] {@attr show-dropdown} Toggle show dropdown chevron on the button.
 * @example
 * <ui-header-button label="Header button" layout="primary" glyph="redesign-circle-user-adult" show-dropdown="true">
 * </ui-header-button>
 */
@Component({
    tag: 'ui-header-button',
    styles,
})
export class UIHeaderButton extends UIElement {
    /**
     * @type {UIHeaderButtonLayout}
     * @readonly
     */
    static readonly Layout = {
        Primary: 'primary',
        Tonal: 'tonal',
        Tertiary: 'tertiary',
    };

    @Attribute() label!: string;
    @Attribute() glyph: string | undefined;
    @Attribute() layout: string = UIHeaderButton.Layout.Primary;
    @Attribute(Boolean) showDropdown: boolean = false;

    @Child(':scope > button') button!: HTMLButtonElement;

    static get observedAttributes(): Array<string> {
        return ['label', 'aria-expanded'];
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _oldValue: string,
        newValue: string | null
    ): void {
        if (this.state !== ComponentState.Hydrated) {
            return;
        }

        switch (name) {
            case 'label': {
                this.updateLabel(newValue);
                break;
            }
            case 'aria-expanded':
                if (newValue !== null) {
                    this.button.setAttribute('aria-expanded', newValue);
                } else {
                    this.button.removeAttribute('aria-expanded');
                }
                break;
            default:
                break;
        }
    }

    /**
     *
     * @param newValue
     * @private
     */
    private updateLabel(newValue: string | null): void {
        const labelSpan = this.button.querySelector('.ui-header-button__label');
        if (labelSpan) {
            if (newValue) {
                labelSpan.textContent = newValue;
            } else {
                labelSpan.remove();
            }
        } else if (newValue) {
            const span = createElement({
                tagName: 'span',
                classList: { 'ui-header-button__label': true },
                children: [newValue],
            });
            if (span) {
                const dropdownIcon = this.button.querySelector(
                    '.ui-header-button__dropdown-icon'
                );
                this.button.insertBefore(span, dropdownIcon);
            }
        }
    }

    /**
     * Attributes to be excluded from the button element.
     * @private
     */
    private excludedButtonAttributes = new Set<string>([
        'label',
        'glyph',
        'class',
        'id',
        'show-dropdown',
        'layout',
    ]);

    /**
     * Passes click when invoked on host to the nested button. Serves as an API sugar allowing programmatic clicks on the host element.
     * @param {MouseEvent} event
     * @private
     */
    handleHostClick = (event: MouseEvent) => {
        if (this.button && event.target === this) {
            this.button.dispatchEvent(
                new MouseEvent('click', { ...event, bubbles: false })
            );
        }
    };

    /**
     * @private
     */
    subscribeEvents() {
        this.addEventListener('click', this.handleHostClick);
    }

    /**
     * @private
     */
    unsubscribeEvents() {
        this.removeEventListener('click', this.handleHostClick);
    }

    /**
     * @inheritDoc
     */
    render() {
        const genericButtonAttributes = Array.from(this.attributes)
            .filter(
                (attribute: Attr) =>
                    !this.excludedButtonAttributes.has(attribute.name)
            )
            .reduce<Record<string, string>>((collection, attribute: Attr) => {
                collection[attribute.name] = attribute.value;
                return collection;
            }, {});

        const button = element`
            <button type="button" class="ui-header-button__button">
                ${
                    this.glyph &&
                    element`<ui-icon glyph="${this.glyph}"></ui-icon>`
                }
                ${
                    this.label &&
                    element`<span class="ui-header-button__label">${this.label}</span> `
                }
                ${
                    this.showDropdown &&
                    element`<ui-icon glyph="redesign-chevron-down" class="ui-header-button__dropdown-icon"></ui-icon>`
                }
            </button>
        `;

        // Set current host attributes (excluding stop-list) on the button
        setAttributes(button, genericButtonAttributes);
        // Remove current attributes from the host (excluding stop-list)
        removeAttributes(this, Object.keys(genericButtonAttributes));

        this.append(button);
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.subscribeEvents();
    }
}
